import React from 'react';
import {
    Button,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tooltip,
    Chip,
    Box,
    Switch,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    DialogContentText,
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowForwardOutlinedIcon from '@mui/icons-material/ArrowForwardOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import {useNavigate} from 'react-router-dom';
import moment from 'moment';
import _ from 'lodash';
import {useMutationCoordinate} from '../../clients/graphql/graphql.hooks';
import {BROKER_NAME, CONNECTION_STATUS} from '../../shared/portfolio-types';
import {jediUpdateShortCircuited, jediUpdateExecutionType} from "../../clients/graphql/views/pilots-queries";
import {PortfolioLpoa} from '../../clients/graphql/pages/lpoa-queries';
import {hasActiveLpoa} from '../../utils/lpoaStatus';

export interface UserPilotOverviewData {
    userKey: number;
    autoPilotSettingsKey: number;
    pilot: string;
    allocation: number;
    status: string;
    portfolio: {
        brokerName: BROKER_NAME;
        connectionStatus: CONNECTION_STATUS;
        portfolioKey: number;
    };
    portfolioConnectionState: string;
    amountPending: number;
    initiatedAt: Date;
    equilibriumState: string;
    isManualExecution: boolean;
    shortCircuited: boolean;
    forcedManualApproval?: boolean;
    forcedNegativeConsentApproval?: boolean;
}

interface UserPilotOverviewProps {
    data: UserPilotOverviewData;
    lpoaData?: PortfolioLpoa[];
}


export const UserPilotDetailsOverview: React.FC<UserPilotOverviewProps> = ({ data, lpoaData }) => {
    const navigator = useNavigate();
    const [confirmationOpen, setConfirmationOpen] = React.useState(false);
    const [executionTypeDialogOpen, setExecutionTypeDialogOpen] = React.useState(false);

    const [updateMutation] = useMutationCoordinate(jediUpdateShortCircuited, {});
    const [updateExecutionTypeMutation] = useMutationCoordinate(jediUpdateExecutionType, {});

    const handleToggleClick = () => {
        setConfirmationOpen(true);
    };

    const handleClose = () => {
        setConfirmationOpen(false);
    };

    const handleToggleShortCircuit = async () => {
        const variables = {
            autoPilotSettingsKey: data.autoPilotSettingsKey,
            shortCircuited: !data.shortCircuited,
        };

        await updateMutation({ variables });
        setConfirmationOpen(false);
        window.location.reload();
    }

    const handleExecutionTypeToggleClick = () => {
        setExecutionTypeDialogOpen(true);
    };

    const handleExecutionTypeDialogClose = () => {
        setExecutionTypeDialogOpen(false);
    };

    const handleExecutionTypeConfirm = async () => {
        const variables = {
            autoPilotSettingsKey: data.autoPilotSettingsKey,
            useManualExecution: !data.isManualExecution,
        };

        await updateExecutionTypeMutation({ variables });
        setExecutionTypeDialogOpen(false);
        window.location.reload();
    }

    type StyledTableCellProps = {
        children: string,
    }
    const StyledTableCell: React.FC<StyledTableCellProps> = ({ children }) => {
        return (
            <TableCell
                sx={{
                    color: '#6D6D6D',
                    fontFamily: 'Roboto',
                    fontWeight: 500,
                    fontSize: '13px',
                    lineHeight: '12px',
                    letterSpacing: '0.05em',
                    paddingBottom: '.75rem',
                }}
            >
                {children}
            </TableCell>
        );
    };
    return (
        <Paper elevation={1} sx={{
            width: '100%',
            padding: 1,
            height: 1,
            marginTop: 1,
        }}><Grid container spacing={2}>
            <Grid item xs={12}>
                <TableContainer>
                    <Table>
                        <TableBody>
                            <TableRow>
                                <StyledTableCell children={'Customer'}/>
                                <TableCell>
                                    <Button
                                        component="label"
                                        role={undefined}
                                        variant="outlined"
                                        tabIndex={-1}
                                        color='primary'
                                        endIcon={<ArrowForwardOutlinedIcon/>}
                                        onClick={(e) => {
                                            if (e.metaKey || e.ctrlKey) {
                                                window.open(`/users/${data.userKey}`);
                                            } else {
                                                navigator(`/users/${data.userKey}`)}
                                        }}
                                    >
                                        #{data.userKey}
                                    </Button>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <StyledTableCell>Portfolio</StyledTableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Tooltip title={`portfolio is ${_.lowerCase(data.portfolioConnectionState)}`}>
                                            <Button
                                                component="label"
                                                role={undefined}
                                                variant="outlined"
                                                tabIndex={-1}
                                                color={data.portfolioConnectionState === 'DISCONNECTED' ? 'error' : 'success'}
                                                startIcon={data.portfolioConnectionState === 'DISCONNECTED' && (
                                                    <ErrorIcon style={{color: 'red'}}/>
                                                )}
                                                endIcon={<ArrowForwardOutlinedIcon/>}
                                                onClick={(e) => {
                                                    if (e.metaKey || e.ctrlKey) {
                                                        window.open(`/portfolios/${data.portfolio.portfolioKey}`);
                                                    } else {
                                                        navigator(`/portfolios/${data.portfolio.portfolioKey}`)}
                                                }}
                                            >
                                                {_.capitalize(data.portfolio.brokerName)}
                                            </Button>
                                        </Tooltip>
                                        {hasActiveLpoa(lpoaData) && (
                                            <Chip
                                                label="LPOA"
                                                size="small"
                                                sx={{
                                                    backgroundColor: '#e3f2fd',
                                                    color: '#1976d2',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                    height: '24px',
                                                    '& .MuiChip-label': {
                                                        px: 1,
                                                    },
                                                }}
                                            />
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <StyledTableCell>Status</StyledTableCell>
                                <TableCell>
                                    <>
                                        {data.status === 'ACTIVE' && (
                                            <Tooltip title="Active">
                                                <Button
                                                    disableRipple={true}
                                                    component="label"
                                                    role={undefined}
                                                    variant="outlined"
                                                    tabIndex={-1}
                                                    color='success'
                                                    startIcon={<CheckCircleOutlinedIcon
                                                        style={{color: 'success'}}/>}
                                                >Active</Button>
                                            </Tooltip>
                                        )}
                                        {data.status === 'DELETED' && (
                                            <Tooltip title="Deleted">
                                                <Button
                                                    disableRipple={true}
                                                    component="label"
                                                    role={undefined}
                                                    variant="outlined"
                                                    tabIndex={-1}
                                                    color='error'
                                                    startIcon={<DeleteOutlinedIcon style={{color: 'warning'}}/>}
                                                >Deleted</Button>
                                            </Tooltip>
                                        )}
                                        {!['DELETED', 'ACTIVE'].includes(data.status) && (
                                            <span>{data.status.split('_').map(_.capitalize).join(' ')}</span>
                                        )}
                                    </>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <StyledTableCell>Tracker Enabled</StyledTableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Tooltip title={data.shortCircuited && data.status === 'ACTIVE' ? "Short circuited - autopilot disabled. Click to re-enable" : !data.shortCircuited && data.status === 'ACTIVE' ? "Active - autopilot enabled. Click to short circuit" : "Short circuit toggle unavailable"}>
                                            <Switch
                                                checked={!data.shortCircuited}
                                                onChange={handleToggleClick}
                                                color={data.shortCircuited ? 'error' : 'success'}
                                            />
                                        </Tooltip>
                                        {data.shortCircuited && (
                                            <ErrorIcon style={{color: 'red', fontSize: '20px'}} />
                                        )}
                                        <span style={{color: data.shortCircuited ? 'red' : 'inherit'}}>
                                            {data.shortCircuited ? 'Short Circuited' : 'Active'}
                                        </span>
                                    </Box>
                                </TableCell>
                            </TableRow>
                            <TableRow>
                                <StyledTableCell> Allocation</StyledTableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>${data.allocation.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                }).slice(1)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <StyledTableCell>Amount Pending</StyledTableCell>
                                <TableCell sx={{fontWeight: 'bold'}}>${data.amountPending.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                }).slice(1)}</TableCell>
                            </TableRow>
                            <TableRow>
                                <StyledTableCell>Started At</StyledTableCell>
                                <TableCell>{moment(data.initiatedAt).format('MM/DD/YYYY HH:MM')}</TableCell>
                            </TableRow>
                            <TableRow>
                                <StyledTableCell>Execution Type</StyledTableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Tooltip title={data.isManualExecution ? "Manual execution - orders require approval. Click to switch to automatic" : "Automatic execution - orders execute immediately. Click to switch to manual"}>
                                            <Switch
                                                checked={!data.isManualExecution}
                                                onChange={handleExecutionTypeToggleClick}
                                                color={data.isManualExecution ? 'default' : 'success'}
                                            />
                                        </Tooltip>
                                        <span>
                                            {data.isManualExecution ? 'MANUAL' : 'AUTOMATIC'}
                                        </span>
                                    </Box>
                                </TableCell>
                            </TableRow>
                            {data.forcedManualApproval !== undefined && data.forcedManualApproval !== null && (
                                <TableRow>
                                    <StyledTableCell>Forced Manual Approval</StyledTableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {data.forcedManualApproval ? (
                                                <>
                                                    <CheckCircleOutlinedIcon style={{ color: '#f59e0b' }} />
                                                    <span style={{ color: '#f59e0b' }}>Enabled</span>
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCircleOutlinedIcon style={{ color: 'green' }} />
                                                    <span>Disabled</span>
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                            {data.forcedNegativeConsentApproval !== undefined && data.forcedNegativeConsentApproval !== null && (
                                <TableRow>
                                    <StyledTableCell>Forced Negative Consent</StyledTableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {data.forcedNegativeConsentApproval ? (
                                                <>
                                                    <CheckCircleOutlinedIcon style={{ color: '#f59e0b' }} />
                                                    <span style={{ color: '#f59e0b' }}>Enabled</span>
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCircleOutlinedIcon style={{ color: 'green' }} />
                                                    <span>Disabled</span>
                                                </>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>
        </Grid>

        {/* Confirmation Dialog */}
        <Dialog open={confirmationOpen} onClose={handleClose}>
            <DialogTitle>
                {!data.shortCircuited ? 'Short Circuit Autopilot?' : 'Re-enable Autopilot?'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {!data.shortCircuited
                        ? 'Are you sure you want to short circuit (disable) autopilot for this customer? This will prevent trades from executing.'
                        : 'Are you sure you want to re-enable autopilot for this customer?'
                    }
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleToggleShortCircuit} variant="contained" color={!data.shortCircuited ? 'error' : 'primary'}>
                    {!data.shortCircuited ? 'Short Circuit' : 'Re-enable'}
                </Button>
            </DialogActions>
        </Dialog>

        {/* Execution Type Confirmation Dialog */}
        <Dialog open={executionTypeDialogOpen} onClose={handleExecutionTypeDialogClose}>
            <DialogTitle>
                {data.isManualExecution ? 'Switch to Automatic Execution?' : 'Switch to Manual Execution?'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {data.isManualExecution
                        ? 'Are you sure you want to switch to automatic execution? Orders will execute immediately without requiring approval.'
                        : 'Are you sure you want to switch to manual execution? Orders will require approval before executing.'
                    }
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleExecutionTypeDialogClose}>Cancel</Button>
                <Button onClick={handleExecutionTypeConfirm} variant="contained" color="primary">
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
        </Paper>
    );
};
